<template>
	<div
		class="accordion-list"
		v-if="list.length"
	>
		<Accordion
			v-for="(item, index) in list"
			ref="accordion"
			:key="`${index}-${defaultExpanded}`"
			:label="listLabel ? (util.isFunction(listLabel) ? listLabel(item) : item[listLabel]) : item"
			:index="closeOtherOnOpen ? index : null"
			:updateSelectedAccordion="updateSelectedAccordion"
			:disabled="customDisable != null ? customDisable(item) : false"
			:defaultExpanded="defaultExpanded ? defaultExpanded : false"
			:selectedAccordion
			@closed="() => handleClosed(index)"
			@opened="() => handleOpened(index)"
		>
			<template #accordionHeader>
				<slot
					name="listHeader"
					:headerItem="item"
					:index="index"
				/>
			</template>
			<template #accordionBody>
				<slot
					name="listBody"
					:bodyItem="item"
					:index="index"
				/>
			</template>
		</Accordion>
	</div>
</template>
<script lang="ts">
	export interface IAccordionListProps {
		list: any[];
		listLabel?: any;
		closeOtherOnOpen?: boolean;
		customDisable?: (item: any) => boolean;
		defaultExpanded?: boolean;
	}
</script>
<script setup lang="ts">
	import Accordion from '@core/components/Accordion.vue';
	import { onMounted, ref, useTemplateRef, watch } from 'vue';
	import util from '@core/services/util';

	const emits = defineEmits(['closed', 'opened', 'change']);
	const { list, listLabel, closeOtherOnOpen, customDisable, defaultExpanded } = defineProps<IAccordionListProps>();

	const selectedAccordion = ref<number>(null);
	const lastSelectedAccordion = ref<number>(null);
	const accordions = ref<typeof accordionRef.value>(null);
	const control = ref({
		opened: false,
		closed: false,
	});

	const accordionRef = useTemplateRef('accordion');
	watch(
		() => control.value,
		newValue => {
			if (newValue.opened && newValue.closed) {
				emits('closed');
				emits('opened');
				control.value.opened = false;
				control.value.closed = false;
			}
		},
		{ deep: true },
	);
	onMounted(() => {
		accordions.value = accordionRef.value;
	});

	function handleOpened(index: number) {
		if (lastSelectedAccordion.value == null || lastSelectedAccordion.value === index || !closeOtherOnOpen) {
			emits('opened', list[index]);
		} else {
			control.value.opened = true;
		}
	}

	function handleClosed(index: number) {
		if (selectedAccordion.value == null || !closeOtherOnOpen) {
			emits('closed', list[index]);
		} else {
			control.value.closed = true;
		}
	}

	function updateSelectedAccordion(index: number) {
		lastSelectedAccordion.value = selectedAccordion.value;
		if (selectedAccordion.value === index) {
			selectedAccordion.value = null;
		} else {
			selectedAccordion.value = index;
		}
		emits('change', selectedAccordion.value);
	}

	function closeAll() {
		accordionRef.value.forEach(accordion => {
			if (accordion?.isExpanded) accordion?.toggle();
		});
	}

	function openAll() {
		accordionRef.value.forEach(accordion => {
			if (!accordion?.isExpanded) accordion?.toggle();
		});
	}
	defineExpose({
		selectedAccordion,
		accordions,
		openAll,
		closeAll,
	});
</script>
